import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { chatApi } from '../api/chatApi';
import { chatService } from '../services/chatService';
import type { Chat, MessageData } from '../types/chat';
import { ErrorService } from '../services/errorService';

interface ErrorOptions {
  severity?: 'low' | 'medium' | 'high';
  userMessage?: string;
  shouldReport?: boolean;
  context?: Record<string, any>;
  recovery?: () => Promise<void>;
}

interface ChatStore {
  // State
  chats: Chat[];
  currentChat: Chat | null;
  messages: MessageData[];
  isLoading: boolean;
  isGeneratingQuestions: boolean;
  hasGeneratedQuestions: boolean;
  inputValue: string;
  pendingMessages: MessageData[];

  // UI State
  isShareDialogOpen: boolean;
  isDrawerOpen: boolean;

  // Streaming State
  streaming: {
    message: string | null;
    questions: string | null;
    verses: Record<string, any> | null;
    error: boolean;
  };

  // Error Handling
  handleAsyncError: <T>(promise: Promise<T>, options: ErrorOptions) => Promise<T | null>;
  handleError: (error: unknown, options?: ErrorOptions) => Promise<null>;
  
  // Chat Management
  loadChats: () => Promise<void>;
  loadChat: (chatId: string) => Promise<void>;
  setCurrentChat: (chat: Chat | null) => void;
  createChat: (content: string, userId: string) => Promise<Chat>;
  deleteChat: (chatId: string) => Promise<void>;
  
  // Message Management
  setMessages: (messages: MessageData[]) => void;
  loadMessages: (chatId: string) => Promise<void>;
  sendMessage: (content: string, threadId: string, chatId: string) => Promise<void>;
  retryLastMessage: (threadId: string, chatId: string) => Promise<void>;
  
  // Special Actions
  tellMeMore: (verse: { book: string; chapter: string; verse: string }, threadId: string, chatId: string) => Promise<void>;
  followUpQuestion: (question: string, threadId: string, chatId: string) => Promise<void>;

  // UI Actions
  setShareDialogOpen: (isOpen: boolean) => void;
  setInputValue: (value: string) => void;
  setDrawerOpen: (isOpen: boolean) => void;
  
  // Cleanup
  clearState: () => void;

  addPendingMessage: (message: MessageData) => void;
  clearPendingMessages: () => void;

  isLoadingMoreChats: boolean;
  hasMoreChats: boolean;
  currentPage: number;

  loadMoreChats: () => Promise<void>;
  deleteAllChats: () => Promise<void>;
  isMessageLoading: boolean;
}

export const useChatStore = create<ChatStore>()(
  devtools((set, get) => ({
    // Initial State
    chats: [],
    currentChat: null,
    messages: [],
    isLoading: false,
    isGeneratingQuestions: false,
    hasGeneratedQuestions: false,
    inputValue: '',
    pendingMessages: [],

    // UI State
    isShareDialogOpen: false,
    isDrawerOpen: false,

    // Streaming State
    streaming: {
      message: null,
      questions: null,
      verses: null,
      error: false
    },

    // Error Handling
    handleAsyncError: async <T>(promise: Promise<T>, options: ErrorOptions): Promise<T | null> => {
      try {
        return await promise;
      } catch (error) {
        console.error('Error handling async operation:', error);
        return null;
      }
    },

    handleError: async (error, options = {}) => {
      return ErrorService.handle(error, {
        ...options,
        recovery: async () => {
          // Default recovery actions
          set({ 
            isLoading: false,
            streaming: {
              message: null,
              questions: null,
              verses: null,
              error: false
            }
          });
        }
      });
    },

    // Chat Management
    loadChats: async () => {
      try {
        // Check if we already have chats loaded to prevent duplicate calls
        const currentChats = get().chats;
        if (currentChats && currentChats.length > 0) {
          console.debug('Chats already loaded, skipping fetch');
          return;
        }

        console.log('Loading chats...');
        const chats = await chatApi.getChats();
        console.log('Loaded chats:', chats);
        set({ chats });
      } catch (error) {
        console.error('Error loading chats:', error);
        throw error;
      }
    },

    loadChat: async (chatId: string) => {
      try {
        const chat = await chatApi.getChat(chatId);
        if (chat) {
          set((state) => {
            return { currentChat: chat };
          });
        }
      } catch (error) {
        throw error;
      }
    },

    setCurrentChat: (chat) => set({ currentChat: chat }),

    createChat: async (content: string, userId: string) => {
      try {
        const chat = await chatService.createChat(content, userId);
        set(state => ({
          chats: Array.isArray(state.chats) ? [chat, ...state.chats] : [chat],
          currentChat: chat
        }));
        return chat;
      } catch (error) {
        throw error;
      }
    },

    deleteChat: async (chatId: string) => {
      try {
        await chatApi.deleteChat(chatId);
        set(state => ({
          chats: state.chats.filter(chat => chat.id !== chatId),
          currentChat: state.currentChat?.id === chatId ? null : state.currentChat
        }));
      } catch (error) {
        console.error('Error deleting chat:', error);
        throw error;
      }
    },

    // Message Management
    setMessages: (messages) => set({ messages }),

    loadMessages: async (chatId: string) => {
      try {
        const messages = await chatApi.getMessages(chatId);
        set(state => {
          // Remove optimistic messages that have matching persisted messages
          const optimisticMessages = state.messages.filter(m => {
            const isOptimistic = m.id.startsWith('temp-');
            const hasPersistedVersion = messages.some((sm: MessageData) => 
              sm.content === m.content && 
              sm.role === m.role
            );
            return isOptimistic && !hasPersistedVersion;
          });
          
          return {
            messages: [...messages, ...optimisticMessages]
          };
        });
      } catch (error) {
        throw error;
      }
    },

    sendMessage: async (content: string, threadId: string, chatId: string) => {
      set({ 
        isMessageLoading: true,
        streaming: {
          message: null,
          questions: null,
          verses: null,
          error: false
        }
      });

      // Add optimistic user message
      const optimisticMessage = {
        id: 'temp-' + Date.now(),
        content,
        role: 'user',
        chat_id: chatId,
        created_at: new Date().toISOString(),
        isOptimistic: true
      };

      set(state => ({
        messages: [...(state.messages || []), optimisticMessage]
      }));

      try {
        await chatService.sendMessage(content, threadId, chatId);
      } catch (error) {
        console.error('[ChatStore] Error sending message:', error);
        set(state => ({
          messages: state.messages.filter(m => m.id !== optimisticMessage.id),
          isLoading: false,
          isMessageLoading: false,
          streaming: {
            message: null,
            questions: null,
            verses: null,
            error: true
          }
        }));
        throw error;
      }
    },

    retryLastMessage: async (threadId: string, chatId: string) => {
      set({ 
        isLoading: true,
        streaming: {
          message: null,
          questions: null,
          verses: null,
          error: false
        }
      });

      try {
        // Get the last user message
        const lastUserMessage = get().messages
          .filter(m => m.role === 'user')
          .pop();

        if (!lastUserMessage) throw new Error('No message to retry');

        // Retry the message using sendMessage
        await get().sendMessage(lastUserMessage.content, threadId, chatId);

        // Generate follow-up questions if this was the first message
        if (get().messages.length <= 2) {  // User message + Assistant response
          set({ isGeneratingQuestions: true });
          try {
            await chatService.generateFollowUpQuestions(
              chatId,
              lastUserMessage.content,
              (content: string) => set({ 
                streaming: {
                  ...get().streaming,
                  questions: content
                }
              })
            );
          } finally {
            set({ 
              isGeneratingQuestions: false,
              streaming: {
                ...get().streaming,
                questions: null
              },
              hasGeneratedQuestions: true
            });
          }
        }
      } catch (error) {
        set({ 
          isLoading: false,
          streaming: {
            message: null,
            questions: null,
            verses: null,
            error: true
          }
        });
        throw error;
      }
    },

    // Special Actions
    tellMeMore: async (verse, threadId, chatId) => {
      const content = `Tell me more about ${verse.book} ${verse.chapter}:${verse.verse}`;
      return get().sendMessage(content, threadId, chatId);
    },

    followUpQuestion: async (question, threadId, chatId) => {
      return get().sendMessage(question, threadId, chatId);
    },

    // UI Actions
    setShareDialogOpen: (isOpen) => set({ isShareDialogOpen: isOpen }),
    setInputValue: (value) => set({ inputValue: value }),
    setDrawerOpen: (isOpen) => set({ isDrawerOpen: isOpen }),

    // Cleanup
    clearState: () => set({
      currentChat: null,
      messages: [],
      activeChat: null,
      streaming: {
        chatId: null,
        message: null,
        questions: null,
        verses: null
      },
      streamingError: false,
      isGeneratingQuestions: false,
      isMessageLoading: false,
      inputValue: '',
      isLoading: false
    }),

    addPendingMessage: (message) => 
      set(state => ({ pendingMessages: [...state.pendingMessages, message] })),

    clearPendingMessages: () => 
      set({ pendingMessages: [] }),

    deleteAllChats: async () => {
      try {
        await chatApi.deleteAllChats(get().chats[0]?.user_id);
        set({
          chats: [],
          currentChat: null,
          messages: [],
          hasMoreChats: false,
          currentPage: 1
        });
      } catch (error) {
        console.error('Error deleting all chats:', error);
        throw error;
      }
    },

    isLoadingMoreChats: false,
    hasMoreChats: false,
    currentPage: 1,

    loadMoreChats: async () => {
      // Implementation for loading more chats
    },

    isMessageLoading: false,
  }))
);